import { useAuth } from '../contexts/AuthContext'
import { apiGet, apiPatch, apiDelete } from '../lib/api'
import { useState, useEffect } from 'react'
import { useNotification } from '../contexts/NotificationContext'

interface User {
  id: string
  email: string
  username: string
  first_name: string
  last_name: string
  phone: string | null
  role: string
  status: string
  email_verified: boolean
  created_at: string
  last_login: string | null
}

export function AdminUsers() {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [newStatus, setNewStatus] = useState<string>('')
  const [newRole, setNewRole] = useState<string>('')

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      fetchUsers()
    }
  }, [user, page])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiGet(`/admin/users?page=${page}&limit=20`)
      const data = await response.json()

      if (response.ok && data.success) {
        setUsers(data.data.users)
        setTotalPages(data.data.pagination.pages)
        setTotal(data.data.pagination.total)
      } else {
        setError(data.error?.message || 'Failed to load users')
      }
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'suspended':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
      case 'superadmin':
        return 'bg-purple-100 text-purple-800'
      case 'user':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleUpdateStatus = async (userId: string, status: string) => {
    try {
      const response = await apiPatch(`/admin/users/${userId}/status`, { status })
      const data = await response.json()

      if (response.ok && data.success) {
        showSuccess('User status updated successfully')
        setEditingUser(null)
        fetchUsers()
      } else {
        showError(data.error?.message || 'Failed to update user status')
      }
    } catch (err) {
      console.error('Failed to update user status:', err)
      showError('Failed to update user status')
    }
  }

  const handleUpdateRole = async (userId: string, role: string) => {
    try {
      const response = await apiPatch(`/admin/users/${userId}/role`, { role })
      const data = await response.json()

      if (response.ok && data.success) {
        showSuccess('User role updated successfully')
        setEditingUser(null)
        fetchUsers()
      } else {
        showError(data.error?.message || 'Failed to update user role')
      }
    } catch (err) {
      console.error('Failed to update user role:', err)
      showError('Failed to update user role')
    }
  }

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete user ${userName}? This action cannot be undone.`)) {
      return
    }

    try {
      const response = await apiDelete(`/admin/users/${userId}`)
      const data = await response.json()

      if (response.ok && data.success) {
        showSuccess('User deleted successfully')
        fetchUsers()
      } else {
        showError(data.error?.message || 'Failed to delete user')
      }
    } catch (err) {
      console.error('Failed to delete user:', err)
      showError('Failed to delete user')
    }
  }

  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Access denied. Admin privileges required.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Management</h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">Manage all system users</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email Verified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userItem) => (
                    <tr key={userItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {userItem.first_name} {userItem.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{userItem.email}</div>
                        {userItem.phone && <div className="text-xs text-gray-400">{userItem.phone}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser === userItem.id ? (
                          <select
                            value={newRole || userItem.role}
                            onChange={(e) => setNewRole(e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1"
                            onBlur={() => {
                              if (newRole && newRole !== userItem.role) {
                                handleUpdateRole(userItem.id, newRole)
                              } else {
                                setEditingUser(null)
                                setNewRole('')
                              }
                            }}
                            autoFocus
                          >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                            <option value="superadmin">superadmin</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(
                              userItem.role
                            )}`}
                          >
                            {userItem.role}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser === userItem.id ? (
                          <select
                            value={newStatus || userItem.status}
                            onChange={(e) => setNewStatus(e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1"
                            onBlur={() => {
                              if (newStatus && newStatus !== userItem.status) {
                                handleUpdateStatus(userItem.id, newStatus)
                              } else {
                                setEditingUser(null)
                                setNewStatus('')
                              }
                            }}
                          >
                            <option value="active">active</option>
                            <option value="inactive">inactive</option>
                            <option value="pending">pending</option>
                            <option value="suspended">suspended</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(
                              userItem.status
                            )}`}
                          >
                            {userItem.status}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userItem.email_verified ? (
                          <span className="text-green-600">✓ Verified</span>
                        ) : (
                          <span className="text-yellow-600">Pending</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(userItem.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {editingUser === userItem.id ? (
                          <button
                            onClick={() => {
                              setEditingUser(null)
                              setNewStatus('')
                              setNewRole('')
                            }}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingUser(userItem.id)
                                setNewStatus(userItem.status)
                                setNewRole(userItem.role)
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                            {userItem.id !== user?.id && (
                              <button
                                onClick={() =>
                                  handleDeleteUser(userItem.id, `${userItem.first_name} ${userItem.last_name}`)
                                }
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page {page} of {totalPages} (Total: {total} users)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
